<template>
  <component
    :is="element"
    class="product-prices"
    :class="{ [$class('loading')]: loading }"
  >
    <!-- Compare at price -->
    <span
      v-if="formattedCompareAtPrice && !priceFirst"
      class="product-prices__compare-at-price text-other-sale-strikethrough"
      :class="classCompareAtPrice"
      v-text="formattedCompareAtPrice"
    />

    <!-- Current price -->
    <span
      class="product-prices__price"
      :class="[
        classPrice,
        { 'product-prices__price--on-sale': formattedCompareAtPrice },
      ]"
      v-text="formattedPrice"
    />

    <!-- Compare at price -->
    <span
      v-if="formattedCompareAtPrice && priceFirst"
      class="product-prices__compare-at-price"
      :class="classCompareAtPrice"
      v-text="formattedCompareAtPrice"
    />

    <!-- Sold out -->
    <span
      v-if="available === false"
      class="product-prices__sold-out"
      :class="classPrice"
      v-text="$string('product.prices.sold_out')"
    />
  </component>
</template>

<script>

/**
 * Vue: Product prices (product-prices)
 * -----------------------------------------------------------------------------
 * Global product prices template.
 *
 * @param {Boolean} [available] - If the product is available.
 * @param {String} [classCompareAtPrice] - Comare at price text classes.
 * @param {String} [classPrice] - Price text classes.
 * @param {Boolean} [displayFree] - Display "free" instead of 0
 * @param {Number|Boolean} compareAtPrice - Compare at price, in cents format.
 * @param {String} [element] - Custom element override.
 * @param {Boolean} [loading] - Loading state.
 * @param {Number} price - Product price, in cents format.
 * @param {Boolean} [priceFirst] - Display price before compare at price.
 *
 */

import { getMoneyFormat } from '~/helpers/general'

export default {
  name: 'ProductPrices',

  props: {
    available: {
      type: Boolean,
      default: true,
    },
    classCompareAtPrice: {
      type: String,
      default: 'text-other-sale-s',
    },
    classPrice: {
      type: String,
      default: 'text-body-s-regular',
    },
    compareAtPrice: {
      type: [Boolean, Number],
      default: false,
      required: true,
    },
    displayFree: {
      type: Boolean,
      default: true,
    },
    element: {
      type: String,
      default: 'p',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    price: {
      type: [Boolean, Number],
      default: false,
      required: true,
    },
    priceFirst: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
      moneyFormat: getMoneyFormat(),
    }
  },

  computed: {

    /**
     * Compute compare at price template.
     * @returns {String}
     */
    formattedCompareAtPrice() {
      return this.compareAtPrice && this.price < this.compareAtPrice
        ? this.$formatMoney(this.compareAtPrice, this.moneyFormat)
        : ''
    },

    /**
     * Compute price template.
     * @returns {String}
     */
    formattedPrice() {
      if (this.price === false) {
        return ''
      }

      if (this.price <= 0 && this.displayFree) {
        return this.$string('product.prices.free')
      }

      return this.price || this.price <= 0 ? this.$formatMoney(this.price, this.moneyFormat) : ''
    },
  },

  mounted() {
    this.handleCustomerOrderPage()
  },

  methods: {

    /**
     * Force price formatting to use market currency on customer order page
     */
    handleCustomerOrderPage() {
      if (this.$variable('page.type') !== 'customers/order') {
        return
      }
      const ogFormat = this.moneyFormat
      const newFormat = ogFormat.slice(
        ogFormat.indexOf('{{'),
        ogFormat.indexOf('}}') + 2,
      )

      this.moneyFormat = `${this.$variable('store.currency')} ${newFormat.trim()}`
    },
  },
}
</script>
